import axios from 'axios'
import type { UserDecathlon } from 'src/components/account/types'

import { axiosClient } from '../../services/axiosInstance'
import { getDyCuid } from './upsertDyUserData'

export const OFFLINE_PURCHASES_SENT_CAP = 200

export type OfflinePurchaseCartItem = {
  productId: string
  quantity: number
  itemPrice: number
  transactionItemValue: number
}

export type OfflinePurchaseTransaction = {
  uniqueTransactionId: string
  transactionSource: string
  transactionDatetime: string
  currency: string
  value: number
  cart: OfflinePurchaseCartItem[]
}

export type OfflinePurchasesPayload = {
  cuid: string
  transactions: OfflinePurchaseTransaction[]
}

type SaleItemLike = {
  sku?: string | null
  skuId?: string | number | null
  quantity?: number | null
  unit_price?: number | null
  amount?: number | null
}

function resolveOfflinePurchaseProductId(item: SaleItemLike): string | null {
  const sku = item.sku?.trim()

  if (sku) return sku

  const { skuId } = item

  if (skuId == null) return null

  const asString = String(skuId).trim()

  return asString.length > 0 ? asString : null
}

const ISO_WITH_OFFSET =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i

const ISO_WITHOUT_OFFSET = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d+)?$/

/** e.g. `2025-04-19T20:00:13-0300` → `2025-04-19T20:00:13-03:00` */
function normalizeCompactTimezoneOffset(value: string) {
  return value.replace(
    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?)([+-])(\d{2})(\d{2})$/,
    '$1$2$3:$4'
  )
}

export function getSentIdsStorageKey(memberId: string) {
  return `dy_offline_purchases_sent_${memberId}`
}

export function getAttemptStorageKey(memberId: string) {
  return `dy-offline-purchases-attempted:${memberId}`
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isPositiveInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

export function toIsoDateTimeWithOffset(value?: string | null): string | null {
  const trimmed = value?.trim()

  if (!trimmed) return null

  const normalized = normalizeCompactTimezoneOffset(trimmed)

  if (ISO_WITH_OFFSET.test(normalized)) {
    const date = new Date(normalized)

    if (Number.isNaN(date.getTime())) return null

    return date.toISOString().replace(/\.\d{3}Z$/, '+00:00')
  }

  const withoutOffset = normalized.match(ISO_WITHOUT_OFFSET)

  if (!withoutOffset) return null

  return `${withoutOffset[1]}+00:00`
}

function normalizeSaleItems(saleItems: unknown): SaleItemLike[] {
  if (Array.isArray(saleItems)) {
    return saleItems.filter(Boolean) as SaleItemLike[]
  }

  if (saleItems && typeof saleItems === 'object') {
    return [saleItems as SaleItemLike]
  }

  return []
}

function mapCartItem(item: SaleItemLike): OfflinePurchaseCartItem | null {
  const productId = resolveOfflinePurchaseProductId(item)

  if (!productId) return null
  if (!isPositiveInt(item.quantity)) return null
  if (!isNonNegativeNumber(item.unit_price)) return null
  if (!isNonNegativeNumber(item.amount)) return null

  return {
    productId,
    quantity: item.quantity,
    itemPrice: item.unit_price,
    transactionItemValue: item.amount,
  }
}

export function readSentTransactionIds(memberId: string): string[] {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(getSentIdsStorageKey(memberId))

    if (!raw) return []

    const parsed = JSON.parse(raw) as unknown

    if (!Array.isArray(parsed)) return []

    return parsed.filter(
      (id): id is string => typeof id === 'string' && id.length > 0
    )
  } catch {
    return []
  }
}

export function persistSentTransactionIds(
  memberId: string,
  transactionIds: string[]
) {
  if (typeof window === 'undefined' || transactionIds.length === 0) return

  const merged = [
    ...readSentTransactionIds(memberId).filter(
      (id) => !transactionIds.includes(id)
    ),
    ...transactionIds,
  ].slice(-OFFLINE_PURCHASES_SENT_CAP)

  window.localStorage.setItem(
    getSentIdsStorageKey(memberId),
    JSON.stringify(merged)
  )
}

export function hasOfflinePurchasesAttempted(memberId: string) {
  if (typeof window === 'undefined') return false

  return window.sessionStorage.getItem(getAttemptStorageKey(memberId)) === '1'
}

export function markOfflinePurchasesAttempted(memberId: string) {
  if (typeof window === 'undefined') return

  window.sessionStorage.setItem(getAttemptStorageKey(memberId), '1')
}

export function clearOfflinePurchasesAttempt(memberId?: string | null) {
  if (typeof window === 'undefined' || !memberId) return

  window.sessionStorage.removeItem(getAttemptStorageKey(memberId))
}

export function mapGetPurchaseToOfflinePayload(
  user: UserDecathlon | null | undefined,
  sentIds: Set<string> = new Set()
): OfflinePurchasesPayload | null {
  const cuid = getDyCuid(user)

  if (!cuid) return null

  const transactions = (user?.getPurchase ?? [])
    .map((purchase) => {
      const transaction = purchase?.transaction
      const uniqueTransactionId = transaction?.transaction_id?.trim()
      const transactionSource = transaction?.business_unit_name?.trim()
      const transactionDatetime = toIsoDateTimeWithOffset(
        transaction?.transaction_date_time
      )

      const currency = transaction?.currency?.trim() || 'BRL'
      const value = transaction?.amount
      const cart = normalizeSaleItems(transaction?.sale_items)
        .map(mapCartItem)
        .filter((item): item is OfflinePurchaseCartItem => item != null)

      if (!uniqueTransactionId || sentIds.has(uniqueTransactionId)) {
        return null
      }

      if (!transactionSource || !transactionDatetime) return null
      if (!isNonNegativeNumber(value) || cart.length === 0) return null

      return {
        uniqueTransactionId,
        transactionSource,
        transactionDatetime,
        currency,
        value,
        cart,
      }
    })
    .filter((item): item is OfflinePurchaseTransaction => item != null)

  if (transactions.length === 0) return null

  return { cuid, transactions }
}

export async function ingestDyOfflinePurchases(
  payload: OfflinePurchasesPayload | null
): Promise<boolean> {
  if (!payload?.cuid?.trim() || payload.transactions.length === 0) {
    return false
  }

  try {
    const response = await axiosClient.post(
      '/api/account/ingestDyOfflinePurchases',
      payload
    )

    return response.status >= 200 && response.status < 300
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.warn('[dy-offline-purchases] ingest failed', {
        status: error.response?.status,
        code: error.code,
      })

      return false
    }

    console.warn('[dy-offline-purchases] ingest failed')

    return false
  }
}

export async function ingestPendingDyOfflinePurchases(
  user: UserDecathlon | null | undefined
): Promise<void> {
  const cuid = getDyCuid(user)

  if (!cuid) return
  if (hasOfflinePurchasesAttempted(cuid)) return

  markOfflinePurchasesAttempted(cuid)

  const sentIds = new Set(readSentTransactionIds(cuid))
  const payload = mapGetPurchaseToOfflinePayload(user, sentIds)

  if (!payload) return

  const ok = await ingestDyOfflinePurchases(payload)

  if (!ok) return

  persistSentTransactionIds(
    cuid,
    payload.transactions.map((transaction) => transaction.uniqueTransactionId)
  )
}
